import { LitElement, html, nothing } from 'lit';
import { customElement, property, query, queryAssignedElements } from 'lit/decorators.js';
import arrow from '@popperjs/core/lib/modifiers/arrow.js';
import { PopoverEvents, PopoverProps, PopoverTrigger } from './props';
import * as TagNames from './popover.tagnames';
import { Placement, createPopper  } from '@popperjs/core/lib';
import flip from '@popperjs/core/lib/modifiers/flip.js';
import PopoverStyles from './popover.scss?inline';


interface PopperPopover {
    update: () => void;
}

@customElement(TagNames.Popover)
export class Popover extends LitElement implements PopoverProps {
    static styles = [PopoverStyles];

    @property({ type: String })
    placement:Placement = 'top';

    @property({ type: String })
    trigger?: PopoverTrigger;

    @property({ type: Boolean })
    disabled?: boolean;

    @property({ type: Boolean, attribute: 'show-caret' })
    showCaret?: boolean;

    @property({ type: Boolean, attribute: 'persistent' })
    persistent?: boolean; 

    @queryAssignedElements({ slot: 'popover-trigger-content', flatten:true })
    triggerElements!: HTMLElement[];

    @query('.floating')
    floatingElement: HTMLElement;

    @query('.arrow')
    arrowElement: HTMLElement;


    get visible() {
        return this.floatingElement && this.floatingElement.classList.contains('show');
    }

    private popoverInstance: PopperPopover;

    firstUpdated() {
        const triggerEl = this.triggerElements?.[0].firstElementChild as HTMLElement;
        this.popoverInstance = createPopper(triggerEl, this.floatingElement, {
            placement: this.placement,
            strategy: 'fixed',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 12],
                    },
                },
                {
                    ...arrow,
                    options: {
                        element: this.arrowElement,
                    },
                },
                flip,
            ]
        });

        if (this.trigger === PopoverTrigger.Click) {
            triggerEl.addEventListener('click', () => this.toggle());
        } else if (this.trigger ===  PopoverTrigger.Hover) {
            triggerEl.addEventListener('mouseenter', () => this.show());
            triggerEl.addEventListener('mouseleave', () => this.hide());
            this.floatingElement?.addEventListener('mouseleave',() =>this.hide());
        } else if (this.trigger === PopoverTrigger.Focus) {
            triggerEl.addEventListener('focus', () => this.show ());
            triggerEl.addEventListener('blur', () => this.hide());
        }
    }

    connectedCallback(): void {
        super.connectedCallback();

        this.addEventListener(PopoverEvents.PopoverHide, this.hide, {
            capture: true,
         });
         this.addEventListener(PopoverEvents.PopoverShow, this.show , {
            capture: true,
         });
    }

    override disconnectedCallback() {
        document.removeEventListener('click', this.documentClickHandler, true);
        this.removeEventListener(PopoverEvents.PopoverHide, this.hide, {capture: true});
        this.removeEventListener(PopoverEvents.PopoverShow, this.show, {capture: true});

        super.disconnectedCallback();
    }

    private documentClickHandler = (event: MouseEvent) => {
        if(this.persistent) return;
        const path = event.composedPath();
        const isInsidePopover = path.includes(this.floatingElement);
        const isInsideTrigger = this.triggerElements.some(el => path.includes(el));
        
        if (!isInsidePopover && !isInsideTrigger) {
            this.hide();
        }
    };

    private  show(): void {
        if(this.disabled || this.visible)
            return;

        this.floatingElement.classList.add('show');
        this.popoverInstance.update();
            
        if (this.trigger === PopoverTrigger.Click && !this.persistent) {
        document.addEventListener('click', this.documentClickHandler, true);
    }

        this.dispatchEvent(
            new Event(PopoverEvents.PopoverShown, { bubbles: true, composed: true })
        );
    }

    private hide(): void {
        if(!this.visible) return;

        this.floatingElement.classList.remove('show');

        document.removeEventListener('click', this.documentClickHandler, true);
        this.dispatchEvent(
            new Event(PopoverEvents.PopoverHidden, { bubbles: true, composed: true })
        );
    }


    private onFloatSlotChange(): void {
        if (this.visible) {
            this.popoverInstance.update();
        }
    }

    private toggle(): void {
        if(this.visible){
            this.hide()
        } else{
            this.show();
        }
    }

    render() {
        return html`
         <slot name="popover-trigger-content"></slot> 
         <div class="floating" part="float-container">
          ${this.showCaret ? html`<div class="arrow" part="popover-arrow"></div>` : nothing}
            <slot @slotchange=${this.onFloatSlotChange} name="popover-content"></slot>
         </div>
      `;
    }
}